import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Plus, Calendar, Tag, CheckCircle, Clock, XCircle, 
  Edit, Trash2, ChevronRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  DRAFT: { label: "Bozza", icon: Clock, color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
  ACTIVE: { label: "Attiva", icon: CheckCircle, color: "bg-green-500/20 text-green-400 border-green-500/30" },
  ENDED: { label: "Terminata", icon: XCircle, color: "bg-red-500/20 text-red-400 border-red-500/30" }
};

export default function AdminCampaigns({ user }) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    status: "DRAFT"
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${API}/admin/campaigns`, { });
      if (response.ok) {
        setCampaigns(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento campagne");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    try {
      const url = editingCampaign 
        ? `${API}/admin/campaigns/${editingCampaign.campaign_id}`
        : `${API}/admin/campaigns`;
      
      const response = await authFetch(url, {
        method: editingCampaign ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingCampaign ? "Campagna aggiornata" : "Campagna creata");
        setDialogOpen(false);
        resetForm();
        fetchCampaigns();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (campaignId) => {
    if (!confirm("Sei sicuro di voler eliminare questa campagna?")) return;

    try {
      const response = await authFetch(`${API}/admin/campaigns/${campaignId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Campagna eliminata");
        fetchCampaigns();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      status: "DRAFT"
    });
    setEditingCampaign(null);
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || "",
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      status: campaign.status
    });
    setDialogOpen(true);
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Campagne Punti</h1>
          <div className="text-zinc-500">{campaigns.length} campagne</div>
        </div>
        
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Nuova Campagna
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle>{editingCampaign ? "Modifica Campagna" : "Nuova Campagna"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400">Nome Campagna *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="es. Promo Primavera 2026"
                    className="bg-zinc-950 border-zinc-800 mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Descrizione</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Descrizione della campagna..."
                    className="bg-zinc-950 border-zinc-800 mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Data Inizio *</label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Data Fine *</label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Stato</label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="DRAFT">Bozza</SelectItem>
                      <SelectItem value="ACTIVE">Attiva</SelectItem>
                      <SelectItem value="ENDED">Terminata</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button onClick={handleSubmit}>
                  {editingCampaign ? "Salva Modifiche" : "Crea Campagna"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <div>Nessuna campagna creata</div>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => {
            const StatusIcon = statusConfig[campaign.status]?.icon || Clock;
            return (
              <div 
                key={campaign.campaign_id}
                className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6 hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-white">{campaign.name}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${statusConfig[campaign.status]?.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[campaign.status]?.label}
                      </span>
                    </div>
                    {campaign.description && (
                      <p className="text-zinc-400 text-sm mb-3">{campaign.description}</p>
                    )}
                    <div className="flex items-center gap-6 text-sm text-zinc-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(campaign.start_date).toLocaleDateString("it-IT")} - {new Date(campaign.end_date).toLocaleDateString("it-IT")}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/admin/campaigns/${campaign.campaign_id}`)}
                    >
                      Dettagli
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEdit(campaign)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDelete(campaign.campaign_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
